import { useQuery, useInfiniteQuery } from '@tanstack/react-query';

import { api } from '../../../lib/api-client';

import type { ConflictAlertListResponse, ConflictAlertRaw, ConflictSeverity } from '../types';

export const CONFLICT_ALERTS_KEY = ['conflict-alerts'] as const;

export function useConflictAlerts(
  params: { status?: string; severity?: ConflictSeverity; category?: string } = {},
): {
  data: ConflictAlertListResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
} {
  const query = useQuery({
    queryKey: [...CONFLICT_ALERTS_KEY, 'list', params],
    queryFn: () => api.listConflictAlertsV2(params),
    staleTime: 30 * 1000,
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => void query.refetch(),
  };
}

// Detalhe por id (T7-4) — drawer "Ver detalhes" do Histórico. Gateia o fetch por
// id != null ([[feedback_gatear_fetch_nao_so_render]]); o GET /:id redacta parties
// sem `audit-events-read-sensitive` (devolve partiesRedacted no raw).
export function useConflictAlertById(id: string | null) {
  return useQuery({
    queryKey: [...CONFLICT_ALERTS_KEY, 'detail', id],
    queryFn: () => api.getConflictAlertV2(id!),
    enabled: id !== null,
  });
}

export function useConflictAlertChain(chainOriginId: string | null) {
  return useQuery({
    queryKey: [...CONFLICT_ALERTS_KEY, 'chain', chainOriginId],
    queryFn: () => api.getConflictAlertChain(chainOriginId!),
    enabled: chainOriginId !== null,
  });
}

export function useConflictAlertMcpBlockEvents(id: string | null) {
  return useQuery({
    queryKey: [...CONFLICT_ALERTS_KEY, 'mcp-events', id],
    queryFn: () => api.getConflictAlertMcpBlockEvents(id!),
    enabled: id !== null,
  });
}

export function useConflictAlertMetrics(params: { periodFrom?: string; periodTo?: string } = {}) {
  return useQuery({
    queryKey: [...CONFLICT_ALERTS_KEY, 'metrics', params],
    queryFn: () => api.getConflictAlertMetrics(params),
    staleTime: 60 * 1000,
  });
}

// Histórico (cursor) — o consumidor (T7-4) acumula páginas; aqui só a 1ª página por params.
export function useConflictAlertHistory(
  params: {
    type?: string;
    severity?: string;
    status?: string;
    cursor?: string;
    limit?: number;
  } = {},
) {
  return useQuery({
    queryKey: [...CONFLICT_ALERTS_KEY, 'history', params],
    queryFn: () => api.getConflictAlertHistory(params),
  });
}

// Histórico paginado (cursor) — tab Histórico T7-4. Acumula páginas via useInfiniteQuery;
// "Carregar mais" chama fetchNextPage. Achata pages → rows (flatMap data).
export function useConflictAlertHistoryInfinite(
  filters: { type?: string; severity?: string; status?: string } = {},
): {
  rows: ConflictAlertRaw[];
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  isError: boolean;
} {
  const query = useInfiniteQuery({
    queryKey: [...CONFLICT_ALERTS_KEY, 'history-infinite', filters],
    queryFn: ({ pageParam }) =>
      api.getConflictAlertHistory({
        ...filters,
        ...(pageParam != null ? { cursor: pageParam } : {}),
        limit: 25,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  return {
    rows: (query.data?.pages ?? []).flatMap((p) => p.data),
    fetchNextPage: () => void query.fetchNextPage(),
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
